import { useEffect, useState, useContext } from 'react';
import Styles from './InternshipPackInfo.module.scss';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAPI, AuthContext, useCan } from 'components/lib';

/* Global Components */
import { BreadCrumbs } from 'components/global/BreadCrumbs/BreadCrumbs';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { Checkout } from 'components/global/Checkout/Checkout';
import { Notification } from 'components/notifications/notification';

/* UI Libraries */
import { Typography, Drawer, Tooltip } from '@mui/material';
import { CalendarTodayOutlined, KeyboardArrowDown } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import { Tooltip as ReactTooltip } from 'react-tooltip';

function SectionBlock({ title, subtitle, items, showItemDescr, setShowItemDescr, authContext, orgPurchase, status, sectionType, t }) {
    if (!items?.length) return null;
    const getTooltipText = () => {
        return t(`InternshipPack.InternshipPackInfo.purchased.${sectionType}`);
    };

    return (
        <div className={Styles.sectionBlock}>
            <p className={Styles.sectionTitle}>{title}</p>
            <p className={Styles.sectionSubtitle}>{subtitle}</p>
            <div className={Styles.sectionDivider} />
            {items.map((item, index) => {
                const expanded = showItemDescr === item.id;
                const shouldGrayOut =
                    (authContext?.user?.plan === 'free' ||
                        (authContext?.user?.plan === 'hybrid' && (!orgPurchase || status !== 'active'))) &&
                    item.isEnrolled;
                return (
                    <div
                        className={Styles.labList}
                        key={item.id + index}
                        style={{
                            height: 'fit-content',
                        }}
                    >
                        <span
                            className={`${Styles.labHeader}
    ${expanded ? Styles.labHeaderExpanded : ''}
    ${shouldGrayOut ? Styles.labHeaderDisabled : ''}
  `}
                            data-tooltip-id={shouldGrayOut ? 'purchased-tooltip' : undefined}
                            data-tooltip-content={shouldGrayOut ? getTooltipText() : undefined}
                            onClick={() =>
                                expanded ? setShowItemDescr(null) : setShowItemDescr(item.id)
                            }
                        >
                            <div className={Styles.labTitleWrapper}>
                                {item.icon && (
                                    <img
                                        src={`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${item.icon}`}
                                        alt={item.name}
                                        className={Styles.labIcon}
                                    />
                                )}
                                <p className={Styles.labTitle}>{item.name}</p>
                            </div>
                            <KeyboardArrowDown />
                        </span>
                        {expanded && (
                            <div
                                className={`${Styles.labsDescr} ${shouldGrayOut ? Styles.labsDescrDisabled : ''
                                    }`}
                            >
                                <p className={`${Styles.labsDescrText} ${Styles.clamped}`}>
                                    {item.descr}
                                </p>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export function InternshipPackInfo(props) {
    const [internshipList, setInternshipList] = useState([]);
    const [labList, setLabList] = useState([]);
    const [projectList, setProjectList] = useState([]);
    const [certificationList, setCertificationList] = useState([]);
    const [showItemDescr, setShowItemDescr] = useState();
    const [showCheckOut, setShowCheckout] = useState(false);
    const [enrolledItems, setEnrolledItems] = useState(0);
    const [calcPrices, setCalcPrices] = useState({ discount: '', strikedTotal: '', subTotal: '', currencySymbol: '', interval: '' });
    const [buyLoading, setBuyLoading] = useState(false);
    const [selectedInternship, setSelectedInternship] = useState(new Set());

    const authContext = useContext(AuthContext);
    const currencySymbol = authContext?.user?.currencySymbol;
    const navigate = useNavigate();
    const { internship_id } = useParams();
    const { data, loading } = useAPI(`/api/internship/${internship_id}/info`, 'GET');

    const isScholar = useCan('user.months.view');

    useEffect(() => {
        if (data) {
            setInternshipList(data);
            setLabList(data.labs || []);
            setProjectList(data.projects || []);
            setCertificationList(data.certifications || []);
        }
    }, [data]);

    // Build selected items set (not purchased items)
    // Helper to add not-enrolled items by id
    const addNotEnrolled = (items = [], getId) => {
        items
            .filter(item => item.isEnrolled === false)
            .forEach(item => selectedInternship.add(getId(item)));
    };

    // Labs
    addNotEnrolled(labList, lab => lab.id);

    // Projects
    addNotEnrolled(projectList, project => project.id);

    // Certifications
    addNotEnrolled(certificationList, cert => cert.id);

    // Certification labs
    const notPurchasedCertLabIds = certificationList?.flatMap(cert =>
        cert.labs?.labs
            ? cert.labs.labs
                .filter(lab => lab.isEnrolled === false)
                .map(lab => lab.lab_id)
            : []
    ) || [];

    notPurchasedCertLabIds.forEach(id => {
        if (id) selectedInternship.add(id);
    });

    // Calculate prices
    useEffect(() => {
        const pricing = internshipList?.pricing;

        if (!pricing) return;

        const actualPrice = pricing.actual_price ?? 0;
        const price = pricing.price ?? 0;

        const totalDiscount = actualPrice - price;
        const subTotal = price;

        setCalcPrices({
            currencySymbol,
            discount: totalDiscount,
            strikedTotal: actualPrice,
            subTotal: subTotal,
            interval: pricing.interval
        });

    }, [internshipList, currencySymbol]);

    const handleEnroll = (isTrial) => {
        const data = {
            internshipId: internship_id
        }
        setBuyLoading(true)
        const options = {
            method: 'POST',
            url: `/api/internship/enroll`,
            params: {
                isTrial: isTrial ? 1 : 0,
            },
            headers: {
                accept: 'application/json',
            },
            data: data,
        };

        axios
            .request(options)
            .then(function (response) {
                let status = response.status;
                if (status === 201) {
                    toast.dark(props.t('InternshipPack.InternshipPackInfo.toast.success'), {
                        type: 'success',
                        position: 'top-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                    navigate('/internships');
                } else {
                    toast.dark(props.t('InternshipPack.InternshipPackInfo.toast.failed'), {
                        type: 'error',
                        position: 'top-right',
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                }
            })
            .catch((err) => {
                let status = err.response.status;
                if (status === 406) {
                    toast.dark((props.t('InternshipPack.InternshipPackInfo.toast.limit'), { enrollLimit: process.env.REACT_APP_ENROLL_LABS_LIMIT }), {
                        type: 'error',
                        position: 'top-right',
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                } else {
                    toast.dark(props.t('InternshipPack.InternshipPackInfo.toast.error'), {
                        type: 'error',
                        position: 'top-right',
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                }
            })
            .finally(() => setBuyLoading(false));
    }

    return (
        <>
            <ToastContainer
                id="warning-toast"
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <Notification call='internship' />
            <Typography
                variant="h6"
                sx={{ display: 'flex', gap: '16px', padding: '16px', mb: '-15px', color: 'rgb(0, 74, 173)' }}
            >
                <BreadCrumbs
                    onClickPaths={['/internships', '']}
                    crumbs={[
                        'Internships',
                        props.t('InternshipPack.InternshipPackInfo.breadcrumbs.curr', { lpath: internshipList?.name })
                    ]}
                />
            </Typography>

            <div data-testid="InternshipPackInfo-checkout-drawer">
                <Drawer anchor='right' open={showCheckOut} onClose={() => { setShowCheckout(!showCheckOut) }}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'transparent',
                        }
                    }}
                >
                    <div data-testid="InternshipPackInfo-checkoutDrawer" className={Styles.checkoutDrawer}>
                        <Checkout
                            purchaseEndpoint='/api/internship/purchase'
                            checkoutName='internship'
                            availableItems={[internshipList]}
                            individualPricing={false}
                            onClose={() => setShowCheckout(false)}
                            userSelItems={selectedInternship}
                        />
                    </div>
                </Drawer>
            </div>

            {(loading || buyLoading) ? (
                <div data-testid="InternshipPackInfo-loading-backdrop">
                    <LoadingBackdrop isOpen={true} />
                </div>
            ) : (
                <div data-testid="InternshipPackInfo-card-container" className={Styles.CardContainer}>
                    <div data-testid="InternshipPackInfo-cert-details" className={Styles.certDetails}>
                        <div data-testid="InternshipPackInfo-image-container" className={Styles.imageContainer} >
                            <img
                                data-testid="InternshipPackInfo-image"
                                src={`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${internshipList.icon}`}
                                className={Styles.courseImage}
                                alt="internship" />
                        </div>
                        <p data-testid="InternshipPackInfo-cert-name" className={Styles.certName}>{internshipList.name}</p>
                        <p data-testid="InternshipPackInfo-cert-description" className={Styles.certDescr}>{internshipList.descr}</p>
                        <div data-testid="InternshipPackInfo-pricing-design" className={Styles.pricingDesign}>
                            <Typography variant='h5' sx={{ fontWeight: 600 }}>
                                {props.t('InternshipPack.InternshipPackInfo.start')}
                            </Typography>
                            {(authContext?.user?.plan === 'free' || (authContext?.user.plan === 'hybrid' && (internshipList?.orgPurchaseStatus !== 'active'))) && (
                                <div data-testid="InternshipPackInfo-pricing-container" style={{ marginTop: '0.5em', display: 'flex', gap: '1em', alignItems: 'center', padding: '0.5em', width: '100%' }}>
                                    <Typography variant='h6' sx={{ textDecoration: 'line-through', color: 'darkgray' }}>{calcPrices.currencySymbol}{calcPrices.strikedTotal}</Typography>
                                    <Typography variant='h4' sx={{ fontWeight: 600 }}> {calcPrices.currencySymbol}{calcPrices.subTotal}</Typography>
                                    <span id='itemSaving' className={Styles.itemSaving}>{props.t('InternshipPack.InternshipPackInfo.Save')} {calcPrices.currencySymbol}{calcPrices.discount}</span>
                                </div>
                            )}

                            <div data-testid="InternshipPackInfo-button-container" style={{ display: 'flex', gap: '1em', width: '100%', alignItems: 'center', marginTop: '1em' }}>
                                {(authContext?.user?.plan === 'free' || (authContext?.user.plan === 'hybrid' && (internshipList?.orgPurchaseStatus !== 'active'))) ? (
                                    <>
                                        <button data-testid="InternshipPackInfo-buy-button" className={Styles.purchaseBtn} onClick={() => { setShowCheckout(!showCheckOut) }}>
                                            {props.t('InternshipPack.InternshipPackInfo.Buy')}
                                        </button>
                                    </>
                                ) : (
                                    <button data-testid="InternshipPackInfo-enroll-button" className={Styles.enrollBtn} onClick={() => { handleEnroll(false) }}>
                                        {props.t('InternshipPack.InternshipPackInfo.Enroll')}
                                    </button>
                                )}

                            </div>
                        </div>
                        <div data-testid="InternshipPackInfo-access-container" style={{ fontSize: "1em", color: '#1450a0', marginTop: '1.5em', display: 'flex', gap: '1.5em', alignItems: 'center' }}>
                            {isScholar && (
                                <span data-testid="InternshipPackInfo-access-span" style={{ display: 'flex', gap: '0.25em', alignItems: 'center' }}><CalendarTodayOutlined fontSize='small' /> {calcPrices?.interval} Months Access</span>
                            )}
                        </div>
                    </div>

                    <div data-testid="InternshipPackInfo-labsInclude-container" className={Styles.labsInclude}>
                        <div data-testid="InternshipPackInfo-sectionScroll-container" className={Styles.sectionScrollContainer}>
                            <SectionBlock
                                title={props.t('InternshipPack.InternshipPackInfo.certs.title')}
                                subtitle={props.t('InternshipPack.InternshipPackInfo.certs.subtitle')}
                                items={certificationList}
                                showItemDescr={showItemDescr}
                                setShowItemDescr={setShowItemDescr}
                                authContext={authContext}
                                orgPurchase={internshipList?.org_purchase}
                                status={internshipList?.status}
                                sectionType="certification"
                                t={props.t}
                            />
                            <SectionBlock
                                title={props.t('InternshipPack.InternshipPackInfo.project.title')}
                                subtitle={props.t('InternshipPack.InternshipPackInfo.project.subtitle')}
                                items={projectList}
                                showItemDescr={showItemDescr}
                                setShowItemDescr={setShowItemDescr}
                                authContext={authContext}
                                orgPurchase={internshipList?.org_purchase}
                                status={internshipList?.status}
                                sectionType="project"
                                t={props.t}
                            />
                            <SectionBlock
                                title={props.t('InternshipPack.InternshipPackInfo.course.title')}
                                subtitle={props.t('InternshipPack.InternshipPackInfo.course.subtitle')}
                                items={labList}
                                showItemDescr={showItemDescr}
                                setShowItemDescr={setShowItemDescr}
                                authContext={authContext}
                                orgPurchase={internshipList?.org_purchase}
                                status={internshipList?.status}
                                sectionType="course"
                                t={props.t}
                            />
                        </div>
                    </div>
                </div>
            )}

            <ReactTooltip
                id="purchased-tooltip"
                place="left"
            />
        </>
    );
}